// Desktop shortcuts (spec §8.8). Convenience only; everything is reachable by touch.
import { useEffect, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { db } from '@/runtime'
import type { TaskResource } from '@/kinds/task/apply'
import { taskActions } from '@/lib/actions'
import { useUi } from '@/state/ui'
import { undoWithFlash } from '@/lib/undo'
import { pickSuiteByDigit } from '@/components/SuiteSwitcher'

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent)

const GO: Record<string, string> = {
  t: '/',
  i: '/inbox',
  u: '/upcoming',
  p: '/projects',
  r: '/review',
  s: '/someday',
  l: '/log',
  g: '/agents/graph',
  a: '/agents/all',
  e: '/dev/releases',
}

const isTyping = (t: EventTarget | null) => {
  const el = t as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

export const useKeyboard = () => {
  const navigate = useNavigate()
  const pendingG = useRef(false)
  useEffect(() => {
    const onKey = async (e: KeyboardEvent) => {
      const ui = useUi.getState()
      // ⌘K / Ctrl+K works everywhere, even inside inputs
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        ui.setPalette(!ui.paletteOpen)
        return
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '.') {
        e.preventDefault()
        ui.toggleFocus()
        return
      }
      // ⌘Z takes back the last write; inside a text field the browser's own undo wins
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z' && !e.shiftKey && !isTyping(e.target)) {
        e.preventDefault()
        void undoWithFlash()
        return
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault()
        ui.toggleSidebar()
        return
      }
      // ⌃1–⌃9 picks a suite by its place in the switcher, ⌃0 Everything; a Mac only, where
      // Ctrl+digit is free (elsewhere the browser takes it for its tabs)
      if (e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey && /^[0-9]$/.test(e.key) && IS_MAC) {
        if (pickSuiteByDigit(e.key, (to) => void navigate({ to }))) e.preventDefault()
        return
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (isTyping(e.target) || document.querySelector('[role="dialog"]')) return
      const key = e.key
      if (pendingG.current) {
        pendingG.current = false
        if (key === 'w') {
          e.preventDefault()
          ui.setSwitcher(true)
          return
        }
        const to = GO[key]
        if (to) {
          e.preventDefault()
          void navigate({ to })
        }
        return
      }
      const cursorTask = async (): Promise<TaskResource | undefined> => {
        const id = ui.listIds[ui.cursor]
        return id ? ((await db.resources.get(id)) as TaskResource | undefined) : undefined
      }
      switch (key) {
        case 'g':
          pendingG.current = true
          setTimeout(() => (pendingG.current = false), 1200)
          return
        case 'n':
          e.preventDefault()
          ui.setQuickAdd(true)
          return
        case '/':
          e.preventDefault()
          ui.setPalette(true)
          return
        case 'j':
          ui.setCursor(ui.cursor + 1)
          return
        case 'k':
          ui.setCursor(ui.cursor - 1)
          return
        case 'x': {
          const t = await cursorTask()
          if (t) void taskActions.complete(t)
          return
        }
        case 's': {
          const t = await cursorTask()
          if (t) void taskActions.toggleTimer(t)
          return
        }
        case 'e':
        case 'Enter': {
          const t = await cursorTask()
          if (t) void navigate({ to: '/tasks/$id', params: { id: t.id } })
          return
        }
        case 'd': {
          const t = await cursorTask()
          if (t) ui.setMenu({ type: 'defer', taskId: t.id })
          return
        }
        case 'p': {
          const t = await cursorTask()
          if (t) ui.setMenu({ type: 'project', taskId: t.id })
          return
        }
        case '1':
        case '2':
        case '3':
        case '0': {
          const t = await cursorTask()
          if (t) void taskActions.setPriority(t, Number(key))
          return
        }
      }
    }
    const handler = (e: KeyboardEvent) => void onKey(e)
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [navigate])
}

/** Views call this with the ordered ids of the list on screen. */
export const useListNav = (ids: string[]) => {
  const setListIds = useUi((s) => s.setListIds)
  const key = ids.join(',')
  useEffect(() => {
    setListIds(key ? key.split(',') : [])
    return () => setListIds([])
  }, [key, setListIds])
  return useUi((s) => s.cursor)
}
